import { useCallback, useEffect, useRef } from "react";
import type { ScreenCommand } from "@signage/shared";
import { apiGet, screenStream } from "@/lib/api";
import { readLastCommandAt, writeLastCommandAt } from "@/lib/localCache";

export interface ScreenCommandHandlers {
  onReload: () => void;
  onClearCache: () => void;
  onFullscreen: () => void;
}

interface CommandRow {
  id: number;
  screen_id: number;
  command: ScreenCommand;
  created_at: string;
}

/**
 * Live TV commands (reload / clear_cache / fullscreen), mirroring the
 * legacy lastCommands timestamp-diff logic: every applied command's
 * timestamp is persisted, so a screen that was offline when a command fired
 * still catches up by querying for anything newer once it reconnects,
 * instead of silently missing it like a pure broadcast channel would.
 */
export function useScreenCommands(screenId: string, handlers: ScreenCommandHandlers) {
  const numericId = Number(screenId);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const applyCommand = useCallback(
    (command: ScreenCommand, createdAt: string) => {
      const last = readLastCommandAt(screenId);
      if (last && new Date(createdAt) <= new Date(last)) return;
      writeLastCommandAt(screenId, createdAt);

      if (command === "reload") handlersRef.current.onReload();
      else if (command === "clear_cache") handlersRef.current.onClearCache();
      else if (command === "fullscreen") handlersRef.current.onFullscreen();
    },
    [screenId],
  );

  const catchUp = useCallback(async () => {
    const since = readLastCommandAt(screenId);
    if (!since) {
      // No stored timestamp means this device has no missed window to catch
      // up on — it is booting fresh, not returning from an outage. Baseline
      // at "now" instead of replaying history: these are live TV commands,
      // and re-applying an old clear_cache here is what turns a cold boot
      // into a reload loop.
      writeLastCommandAt(screenId, new Date().toISOString());
      return;
    }

    try {
      const rows = await apiGet<CommandRow[]>(
        `/commands?screen=${numericId}&since=${encodeURIComponent(since)}`,
      );
      for (const row of rows) applyCommand(row.command, row.created_at);
    } catch {
      // Offline or server unreachable — the next reconnect retries.
    }
  }, [numericId, screenId, applyCommand]);

  useEffect(() => {
    const stream = screenStream(screenId);
    const offCommand = stream.on("command", (data) => {
      const row = data as CommandRow;
      if (row && row.screen_id === numericId) applyCommand(row.command, row.created_at);
    });
    // Catch up on every (re)connect, which also covers the initial load.
    const offOpen = stream.onOpen(() => void catchUp());
    stream.acquire();

    return () => {
      offCommand();
      offOpen();
      stream.release();
    };
  }, [numericId, screenId, catchUp, applyCommand]);

  return { catchUp };
}
